// Per-store sales converted to GBP at HMRC monthly rates: GBP = local ÷ units per £1.
import { Fragment, useState } from 'react';
import { formatValue } from '../../lib/format';
import { Icon } from '../../ui/Icon';
import type { RegistryEntry, WidgetComponentProps } from '../types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (m: string) => `${MONTHS[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

function CurrencyTable({ data, props }: WidgetComponentProps) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const rows: any[] = data.rows ?? [];
  const multiMonth = (data.months ?? []).length > 1;
  return (
    <div className="table-wrap">
      <table className="data">
        <caption className="sr-only">Sales by store converted to GBP at HMRC monthly rates</caption>
        <thead>
          <tr>
            <th>Store</th>
            <th>Currency</th>
            <th className="num">{data.metric?.label ?? 'Sales'} (store currency)</th>
            <th className="num">HMRC rate (per £1)</th>
            <th className="num">{data.metric?.label ?? 'Sales'} (GBP)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const expandable = multiMonth && props.showMonthlySplit !== false && r.currency !== 'GBP';
            return (
              <Fragment key={r.storeId}>
                <tr>
                  <td>
                    {expandable ? (
                      <button
                        className="btn ghost sm"
                        style={{ padding: 0 }}
                        aria-expanded={open.has(r.storeId)}
                        onClick={() => setOpen((s) => {
                          const n = new Set(s);
                          if (n.has(r.storeId)) n.delete(r.storeId);
                          else n.add(r.storeId);
                          return n;
                        })}
                      >
                        <Icon name={open.has(r.storeId) ? 'chevronUp' : 'chevronDown'} size={12} /> {r.store}
                      </button>
                    ) : (
                      r.store
                    )}
                  </td>
                  <td>{r.currency}</td>
                  <td className="num">{formatValue(r.local, 'currency', { currency: r.currency })}</td>
                  <td className="num">{r.currency === 'GBP' ? '1.0000' : r.rate !== null ? formatValue(r.rate, 'rate') : r.rates.map((x: any) => `${monthLabel(x.month)} ${x.rate ?? 'missing'}`).join(' · ')}</td>
                  <td className="num emph">{r.gbp === null ? <span className="error-text">HMRC rate missing</span> : formatValue(r.gbp, 'currency', { currency: 'GBP' })}</td>
                </tr>
                {open.has(r.storeId) &&
                  r.rates.map((x: any) => (
                    <tr key={x.month}>
                      <td className="muted" style={{ paddingLeft: 28 }}>{monthLabel(x.month)}</td>
                      <td />
                      <td className="num muted">{formatValue(x.local, 'currency', { currency: r.currency })}</td>
                      <td className="num muted">{x.rate ?? 'missing'}</td>
                      <td className="num muted">{formatValue(x.gbp, 'currency', { currency: 'GBP' })}</td>
                    </tr>
                  ))}
              </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4}>Total (GBP)</td>
            <td className="num">{data.total === null ? <span className="error-text">Incomplete — rate missing</span> : formatValue(data.total, 'currency', { currency: 'GBP' })}</td>
          </tr>
        </tfoot>
      </table>
      <div className="hint" style={{ marginTop: 10 }} title={`${data.formula}. Monthly HMRC rates, never live market rates.`}>
        HMRC rate month{(data.months ?? []).length > 1 ? 's' : ''}: {(data.months ?? []).map(monthLabel).join(', ') || '—'}
      </div>
    </div>
  );
}

export const currencyTable: RegistryEntry = { component: CurrencyTable, scroll: true, isEmpty: (d) => !d?.rows?.length };
